import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Equipment from '../components/Equipment';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const ServiciosPage = () => {
  const serviceCategories = [
    {
      id: 'generadores',
      title: 'Renta de Generadores',
      description: 'Generadores eléctricos desde 5kW hasta 1000kW para todo tipo de proyectos',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      link: '/generadores'
    },
    {
      id: 'torres',
      title: 'Renta de Torres de Iluminación',
      description: 'Torres de iluminación para obras nocturnas, eventos y proyectos industriales',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      link: '/servicios/torres-de-iluminacion'
    },
    {
      id: 'soldadoras',
      title: 'Renta de Soldadoras de Combustión y Eléctricas',
      description: 'Soldadoras industriales para proyectos de construcción y manufactura',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
        </svg>
      ),
      link: '/servicios/soldadoras'
    },
    {
      id: 'reparacion',
      title: 'Servicio de Reparación y Mantenimiento',
      description: 'Mantenimiento preventivo y correctivo para generadores y equipos eléctricos',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      link: '/servicios/reparacion-y-mantenimiento'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Servicios de Renta de Generadores | GENSOL Veracruz</title>
        <meta name="description" content="Servicios de renta de generadores eléctricos de 5kW a 1000kW, plantas de luz, torres de iluminación y soldadoras. Entrega e instalación en todo el sureste de México." />
        <meta name="keywords" content="renta generadores, plantas de luz, torres iluminación, soldadoras eléctricas, servicio industrial, Veracruz" />
        <link rel="canonical" href="https://www.gensolver.com/servicios" />
        <meta property="og:title" content="Servicios de Renta de Generadores | GENSOL" />
        <meta property="og:description" content="Amplia gama de generadores y equipos eléctricos en renta. Desde 5kW hasta 1000kW." />
        <meta property="og:url" content="https://www.gensolver.com/servicios" />
      </Helmet>

      <BreadcrumbSchema
        items={[
          { name: 'Inicio', url: 'https://www.gensolver.com/' },
          { name: 'Servicios', url: 'https://www.gensolver.com/servicios' },
        ]}
      />

      <main id="servicios" data-page="servicios" data-analytics-page="servicios" className="pt-24">
        {/* Page Header with Main H1 */}
        <section className="py-12 bg-gray-200">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Nuestros <span className="text-orange-500">Servicios</span>
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Soluciones completas en renta de equipos de generación eléctrica para cualquier necesidad
            </p>
          </div>
        </section>

        {/* Service Categories with H2 Headlines */}
        <section className="py-16 bg-gray-100">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8">
              {serviceCategories.map((service) => (
                <Link 
                  key={service.id}
                  to={service.link}
                  data-testid={`service-${service.id}`}
                  data-analytics-click={`service-category-${service.id}`}
                  className="group bg-white p-8 rounded-2xl border border-gray-200 hover:border-orange-500/50 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-orange-500/20 rounded-xl flex items-center justify-center text-orange-500 group-hover:bg-orange-500 group-hover:text-white transition-all">
                      {service.icon}
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 group-hover:text-orange-500 transition-colors">
                        {service.title}
                      </h2>
                      <p className="text-gray-600">{service.description}</p>
                      <span className="inline-flex items-center mt-4 text-orange-500 font-semibold">
                        Ver más
                        <svg className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <Equipment />

        {/* Additional Services Info */}
        <section className="py-16 bg-gray-200">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              ¿Por qué elegir <span className="text-orange-500">GENSOL</span>?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div 
                className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm"
                data-analytics-element="service-delivery"
              >
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Entrega Rápida</h3>
                <p className="text-gray-600">Entrega e instalación en menos de 24 horas en toda la zona sureste.</p>
              </div>

              <div 
                className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm"
                data-analytics-element="service-support"
              >
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Soporte 24/7</h3>
                <p className="text-gray-600">Técnicos disponibles las 24 horas para cualquier emergencia.</p>
              </div>

              <div 
                className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm"
                data-analytics-element="service-maintenance"
              >
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Mantenimiento Incluido</h3>
                <p className="text-gray-600">Mantenimiento preventivo durante todo el periodo de renta.</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default ServiciosPage;
